import React from 'react'
import { Star } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'

const DEFAULT_API_BASE = import.meta.env.VITE_API_BASE || ''

const decodeTokenPayload = (token: string) => {
  if (!token) return null
  const raw = token.split('.')[0]
  if (!raw) return null
  const normalized = raw.replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4)
  try {
    return JSON.parse(atob(padded))
  } catch {
    return null
  }
}

const buildApiBase = (stack?: string | null) => {
  if (stack) return `https://api.eucamatch.com/${stack}`
  return DEFAULT_API_BASE
}

const formatFallbackDate = (value?: string | null) => {
  if (!value) return ''
  const parsed = Date.parse(value)
  if (!Number.isFinite(parsed)) return ''
  return new Date(parsed).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })
}

type ReviewDetails = {
  rating?: number | null
  comment?: string | null
  createdAt?: string | null
}

export default function ReviewPage() {
  const [searchParams] = useSearchParams()
  const token = (searchParams.get('token') || '').trim()
  const stackParam = (searchParams.get('stack') || '').trim() || null
  const payload = React.useMemo(() => decodeTokenPayload(token), [token])
  const apiBase = React.useMemo(() => {
    const base = buildApiBase(stackParam || payload?.stack || null)
    return base ? base.replace(/\/+$/, '') : base
  }, [stackParam, payload])

  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [errorKind, setErrorKind] = React.useState<'invalid' | 'generic' | null>(null)
  const [providerName, setProviderName] = React.useState('your professional')
  const [bookingLabel, setBookingLabel] = React.useState('')
  const [rating, setRating] = React.useState(0)
  const [hover, setHover] = React.useState(0)
  const [comment, setComment] = React.useState('')
  const [submitted, setSubmitted] = React.useState(false)
  const [alreadyReviewed, setAlreadyReviewed] = React.useState(false)
  const [reviewDetails, setReviewDetails] = React.useState<ReviewDetails | null>(null)

  const normalizeReviewError = (message: string) => {
    const text = String(message || '').trim()
    const lower = text.toLowerCase()
    if (
      lower.includes('invalid') ||
      lower.includes('expired') ||
      lower.includes('missing token') ||
      lower.includes('no longer valid')
    ) {
      return {
        kind: 'invalid' as const,
        message: 'This review link is invalid or has expired.',
      }
    }
    if (!text) {
      return { kind: 'generic' as const, message: 'Unable to load review link.' }
    }
    return { kind: 'generic' as const, message: text }
  }

  React.useEffect(() => {
    if (!token) {
      setError('Missing review link.')
      setErrorKind('invalid')
      return
    }
    if (!apiBase) {
      setError('Review link is missing stack information.')
      setErrorKind('invalid')
      return
    }
    let mounted = true
    setLoading(true)
    setError(null)
    setErrorKind(null)
    fetch(`${apiBase}/public/reviews/providers?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (!res.ok) {
          const msg = (await res.text().catch(() => '')) || res.statusText
          throw new Error(msg)
        }
        return res.json()
      })
      .then((data) => {
        if (!mounted) return
        setProviderName(data?.providerName || 'your professional')
        setBookingLabel(data?.bookingLabel || '')
        if (data?.alreadyReviewed) {
          setAlreadyReviewed(true)
          setReviewDetails(data?.review || null)
        }
      })
      .catch((err: any) => {
        if (!mounted) return
        const normalized = normalizeReviewError(err?.message || '')
        setError(normalized.message)
        setErrorKind(normalized.kind)
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })
    return () => {
      mounted = false
    }
  }, [token, apiBase])

  const submit = async () => {
    if (!token) return
    if (!apiBase) return
    if (!rating) {
      setError('Please select a star rating.')
      setErrorKind('generic')
      return
    }
    setError(null)
    setErrorKind(null)
    setLoading(true)
    try {
      const res = await fetch(`${apiBase}/public/reviews/providers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, rating, comment }),
      })
      if (!res.ok) {
        const msg = (await res.text().catch(() => '')) || res.statusText
        throw new Error(msg)
      }
      await res.json()
      setSubmitted(true)
    } catch (err: any) {
      const normalized = normalizeReviewError(err?.message || '')
      setError(normalized.message)
      setErrorKind(normalized.kind)
    } finally {
      setLoading(false)
    }
  }

  return (
    <section className="info-page review-page">
      <div className="section-header">
        <span className="eyebrow">EUCA Match</span>
        <h2>Rate your experience</h2>
        <p>Share your feedback for {providerName}.</p>
      </div>
      <div className="review-card">
        {bookingLabel && (
          <div className="review-booking">
            <span>Booking</span>
            <strong>{bookingLabel}</strong>
          </div>
        )}
        {error && <div className="banner error">{error}</div>}
        {error && errorKind === 'invalid' && (
          <div className="hint" style={{ marginTop: '.35rem' }}>
            Ask your professional to resend a fresh review link.
          </div>
        )}
        {loading && <div className="banner info">Loading…</div>}

        {alreadyReviewed ? (
          <div className="review-summary">
            <p className="review-summary-title">Review already submitted</p>
            {reviewDetails && (
              <div className="review-stars readonly">
                {Array.from({ length: 5 }).map((_, idx) => {
                  const starValue = idx + 1
                  const active = starValue <= (reviewDetails.rating || 0)
                  return (
                    <Star
                      key={starValue}
                      size={24}
                      className={active ? 'active' : ''}
                      strokeWidth={1.6}
                    />
                  )
                })}
              </div>
            )}
            {reviewDetails?.comment && (
              <div className="review-comment">{reviewDetails.comment}</div>
            )}
            {reviewDetails?.createdAt && (
              <p className="hint">Submitted {formatFallbackDate(reviewDetails.createdAt)}</p>
            )}
          </div>
        ) : submitted ? (
          <div className="review-summary">
            <p className="review-summary-title">Thanks for your review!</p>
            <p className="hint">Your feedback has been sent to EUCA Match.</p>
          </div>
        ) : (
          <>
            <div className="review-stars">
              {Array.from({ length: 5 }).map((_, idx) => {
                const starValue = idx + 1
                const active = starValue <= (hover || rating)
                return (
                  <button
                    key={starValue}
                    type="button"
                    onMouseEnter={() => setHover(starValue)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setRating(starValue)}
                    aria-label={`${starValue} star${starValue === 1 ? '' : 's'}`}
                    className={active ? 'active' : ''}
                  >
                    <Star size={26} strokeWidth={1.6} />
                  </button>
                )
              })}
            </div>
            <div className="review-field">
              <label htmlFor="review-comment">Optional comment</label>
              <textarea
                id="review-comment"
                rows={4}
                maxLength={1000}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Share anything that stood out about your booking."
              />
            </div>
            <button
              type="button"
              className="btn cta"
              onClick={submit}
              disabled={loading}
            >
              Submit review
            </button>
          </>
        )}
      </div>
    </section>
  )
}
